import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import AppBar from "../../components/atoms/appbar";
import {
  useTournamentCalendarList,
  useTournamentDetail,
} from "../../@apis/hooks/useTournament";
import { formatDate } from "../../lib/utils/dateUtils";

// 성별 pill (남성/여성/성별무관)
const genderPill = (gender: number | null) => {
  switch (gender) {
    case 1:
      return { label: "남성", className: "bg-[#daeeff] text-[#007aff]" };
    case 0:
      return { label: "여성", className: "bg-[#ffdada] text-[#ff3b30]" };
    default:
      return { label: "성별무관", className: "bg-[#fff8da] text-[#a2845e]" };
  }
};

export default function CompetitionStatus() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: detailRes } = useQuery({
    ...useTournamentDetail(id as string),
    enabled: !!id,
  });
  const tournamentName = detailRes?.data?.tournamentInfo?.name ?? "";

  const { data: calendarListRes, refetch } = useQuery({
    ...useTournamentCalendarList(id as string),
    enabled: !!id,
  });
  const calendarList = calendarListRes?.data ?? [];

  // 새로고침 클릭 시 아이콘을 제자리에서 한 바퀴 회전시킨다.
  // 클릭할 때마다 key를 갱신해 애니메이션을 재시작한다.
  const [spinKey, setSpinKey] = useState<number | null>(null);
  const handleRefresh = () => {
    setSpinKey(Date.now());
    refetch();
  };

  return (
    <div className="min-h-screen bg-white">
      <AppBar
        title="대회 접수 현황"
        backButton
        onBack={() => navigate(-1)}
        right={
          <button
            aria-label="새로고침"
            onClick={handleRefresh}
            className="grid place-items-center size-[24px]"
          >
            <img
              key={spinKey ?? "idle"}
              className={`size-[22px] ${spinKey ? "animate-[spin_0.6s_linear]" : ""}`}
              onAnimationEnd={() => setSpinKey(null)}
              src="/icons/ic-refresh.png"
              alt="새로고침"
            />
          </button>
        }
      />

      {/* 상단 타이틀 */}
      <div className="p-[20px] flex flex-col gap-[8px]">
        {tournamentName && (
          <p className="text-base font-semibold text-[#8e8e93] break-keep">
            {tournamentName}
          </p>
        )}
        <p className="text-[22px] font-medium text-black">실시간 접수 현황</p>
      </div>

      {calendarList.length === 0 ? (
        <div className="flex min-h-[calc(100dvh-56px-140px)] flex-col items-center justify-center text-[#8e8e93]">
          <p className="text-sm">접수 현황이 없습니다.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-[20px]">
          {calendarList.map(({ date, calendars }) => (
            <div key={date} className="flex flex-col">
              {/* 날짜 헤더 */}
              <div className="bg-[#f2f2f7] px-[20px] py-[8px]">
                <p className="text-base font-semibold text-black">
                  {formatDate(date, "YYYY년 M월 D일 (ddd)")}
                </p>
              </div>

              {calendars.map(
                ({
                  id: calendarId,
                  timeType,
                  gender,
                  applyUsedCount,
                  maxApplyCount,
                }) => {
                  const pill = genderPill(gender);
                  // 정원 미설정(null) 또는 10만 이상이면 정원없음 처리
                  const unlimited = maxApplyCount == null || maxApplyCount >= 100000;
                  return (
                    <div
                      key={calendarId}
                      className="border-b border-[#e5e5ea] p-[20px] flex flex-col gap-[8px]"
                    >
                      <div className="flex gap-[12px] items-center">
                        <p className="text-[22px] font-medium text-black">
                          {timeType}
                        </p>
                        <span
                          className={`px-[4px] rounded-[5px] text-sm font-medium ${pill.className}`}
                        >
                          {pill.label}
                        </span>
                      </div>
                      <div className="flex gap-[4px] items-center text-base font-semibold">
                        <p className="text-black">
                          신청 : {applyUsedCount.toLocaleString()}명
                        </p>
                        <p className="text-[#8e8e93]">
                          {unlimited
                            ? "/ 정원없음"
                            : `/ 정원 : ${maxApplyCount.toLocaleString()}명`}
                        </p>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
